import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { AutoClaimsUpsellModal } from "@redotech/redo-merchant-fulfillment-app/common/shipping-claim/auto-claims-upsell-modal";
import { PopupEvent } from "@redotech/redo-onboarding-common/popup-events";
import { FeatureOnboardingType } from "@redotech/redo-onboarding-common/user-onboarding";
import { assertNever } from "@redotech/util/type";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isWmsRoute } from "../app/wms-route";
import { isWelcomeRoute } from "../onboarding/first-install-redirect";
import * as styles from "./index.module.css";
import { ManagedReturnsTrialModal } from "./managed-returns-trial-onboarding/managed-returns-trial-modal";
import { AgenticCatalogOnboarding } from "./onboarding-cards/agentic-catalog-onboarding";
import { BillingSettingsConsolidatedOnboarding } from "./onboarding-cards/billing-settings-consolidated";
import { ConversionOnboarding } from "./onboarding-cards/conversion-onboarding";
import { ExpandedWarrantyOnboarding } from "./onboarding-cards/expanded-warranty-onboarding";
import { FeedbackAnnouncementOnboarding } from "./onboarding-cards/feedback-announcement";
import { HelpIconMovingOnboarding } from "./onboarding-cards/help-icon-moving";
import { IntercomMovedOnboarding } from "./onboarding-cards/intercom-moved";
import { ManagedReturnsOnboarding } from "./onboarding-cards/managed-returns";
import { PackageProtectionPlusOnboarding } from "./onboarding-cards/package-protection-plus";
import { ReclaimAnnouncementOnboarding } from "./onboarding-cards/reclaim-announcement";
import { SignupFormsReplacedByPopupsOnboarding } from "./onboarding-cards/signup-forms-replaced-by-popups";
import { AiSupportActionNeededV2Modal } from "./onboarding-modals/ai-support-action-needed-v2-modal";
import { DhlFreeLabelsAnnouncementModal } from "./onboarding-modals/dhl-free-labels-announcement-modal";
import { GivingDayTeaserModal } from "./onboarding-modals/giving-day-teaser-modal";
import { HomepageAnalyticsMovedModal } from "./onboarding-modals/homepage-analytics-moved-modal";
import { McpAnnouncementModal } from "./onboarding-modals/mcp-announcement-modal";
import { OutboundLabelsUpsellOnboarding } from "./onboarding-modals/outbound-labels-upsell";
import {
  RecoverNonSubscriberAnnouncementModal,
  RecoverNonSubscriberOffer2500Modal,
  RecoverNonSubscriberOffer5000Modal,
  RecoverSubscriberAnnouncementModal,
} from "./onboarding-modals/recover-product-announcement-modal";
import { Redo3plInterestSurveyOnboarding } from "./onboarding-modals/redo-3pl-interest-survey-modal";
import { ShopperAiWrappedModal } from "./onboarding-modals/shopper-ai-wrapped-modal";
import { SuppliesFreeLabelsAnnouncementModal } from "./onboarding-modals/supplies-free-labels-announcement-modal";
import { SuppliesProductAnnouncementModal } from "./onboarding-modals/supplies-product-announcement-modal";
import { useOnboardingPreviewType } from "./onboarding-preview";
import { isPopupVisibleToMerchant } from "./popup-analytics";
import { useFeatureOnboardingManager } from "./use-feature-onboarding";
import { useWithdrawalsOnboardingRequestId } from "./withdrawals-onboarding-trigger";
import { WithdrawalsOnboardingModal } from "./withdrawals-onboarding/withdrawals-onboarding-modal";

export const FeatureOnboarding = memo(function FeatureOnboarding() {
  const screenType = useScreenType();
  const {
    onboardingType: managerType,
    showOnboarding: managerShow,
    handleDismiss,
    handleComplete,
    dismissOnboardingType,
    completeOnboardingType,
    trackOnboardingEvent,
  } = useFeatureOnboardingManager();

  const location = useLocation();
  const navigate = useNavigate();
  const managedReturnsTrialPreview = useManagedReturnsTrialPreview();
  const withdrawalsOnboardingRequestId = useWithdrawalsOnboardingRequestId();
  const [
    dismissedWithdrawalsOnboardingRequestId,
    setDismissedWithdrawalsOnboardingRequestId,
  ] = useState(0);
  const previewType = useOnboardingPreviewType();

  const manualWithdrawalsOnboardingOpen =
    withdrawalsOnboardingRequestId > dismissedWithdrawalsOnboardingRequestId;
  const onboardingType = manualWithdrawalsOnboardingOpen
    ? FeatureOnboardingType.WITHDRAWALS_ONBOARDING
    : (previewType ?? managerType);
  const showOnboarding = manualWithdrawalsOnboardingOpen ? true : managerShow;

  const card = useMemo(() => {
    const isWithdrawalsOnboarding =
      onboardingType === FeatureOnboardingType.WITHDRAWALS_ONBOARDING;
    if (screenType === ScreenType.MOBILE && !isWithdrawalsOnboarding) {
      return null;
    }
    // Hide from Playwright unless `?onboarding-preview` is set.
    if (
      !isWithdrawalsOnboarding &&
      !previewType &&
      typeof navigator !== "undefined" &&
      navigator.webdriver
    ) {
      return null;
    }
    if (
      !onboardingType ||
      !showOnboarding ||
      onboardingType === FeatureOnboardingType.DISABLED
    ) {
      return null;
    }
    switch (onboardingType) {
      case FeatureOnboardingType.PACKAGE_PROTECTION_PLUS:
        return (
          <PackageProtectionPlusOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.EXPANDED_WARRANTY:
        return (
          <ExpandedWarrantyOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.MANAGED_RETURNS:
        return (
          <ManagedReturnsOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.CONVERSION:
        return (
          <ConversionOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.INTERCOM_MOVED:
        return (
          <IntercomMovedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SUPPORT_TO_AI_UPSELL:
        return (
          <AiSupportActionNeededV2Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SHOPPER_AI_WRAPPED:
        return (
          <ShopperAiWrappedModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED:
        return (
          <BillingSettingsConsolidatedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.HELP_ICON_MOVING:
        return (
          <HelpIconMovingOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.AGENTIC_CATALOG:
        return (
          <AgenticCatalogOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL:
        return (
          <OutboundLabelsUpsellOnboarding
            carrier={onboardingType}
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.HOMEPAGE_ANALYTICS_MOVED:
        return (
          <HomepageAnalyticsMovedModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECLAIM_ANNOUNCEMENT:
        return (
          <ReclaimAnnouncementOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SHIPPING_CLAIMS_AUTO_FILING:
        return (
          <AutoClaimsUpsellModal
            onAccept={handleComplete}
            onClose={handleDismiss}
            open
          />
        );
      case FeatureOnboardingType.SUPPLIES_PRODUCT_ANNOUNCEMENT:
        return (
          <SuppliesProductAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_SUBSCRIBER_ANNOUNCEMENT:
        return (
          <RecoverSubscriberAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_ANNOUNCEMENT:
        return (
          <RecoverNonSubscriberAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_2500:
        return (
          <RecoverNonSubscriberOffer2500Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.RECOVER_NON_SUBSCRIBER_OFFER_5000:
        return (
          <RecoverNonSubscriberOffer5000Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SUPPLIES_FREE_LABELS_ANNOUNCEMENT:
        return (
          <SuppliesFreeLabelsAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.DHL_FREE_LABELS_ANNOUNCEMENT:
        return (
          <DhlFreeLabelsAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT:
        return (
          <FeedbackAnnouncementOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.REDO_3PL_INTEREST_SURVEY:
        return (
          <Redo3plInterestSurveyOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.MANAGED_RETURNS_TRIAL:
        return (
          <ManagedReturnsTrialModal
            onClose={handleDismiss}
            onComplete={handleComplete}
          />
        );
      case FeatureOnboardingType.WITHDRAWALS_ONBOARDING:
        return (
          <WithdrawalsOnboardingModal
            onClose={() => {
              if (manualWithdrawalsOnboardingOpen) {
                setDismissedWithdrawalsOnboardingRequestId(
                  withdrawalsOnboardingRequestId,
                );
                void dismissOnboardingType(
                  FeatureOnboardingType.WITHDRAWALS_ONBOARDING,
                );
                return;
              }
              void handleDismiss();
            }}
            onComplete={() => {
              if (manualWithdrawalsOnboardingOpen) {
                setDismissedWithdrawalsOnboardingRequestId(
                  withdrawalsOnboardingRequestId,
                );
                void completeOnboardingType(
                  FeatureOnboardingType.WITHDRAWALS_ONBOARDING,
                );
                return;
              }
              void handleComplete();
            }}
          />
        );
      case FeatureOnboardingType.MCP_ANNOUNCEMENT:
        return (
          <McpAnnouncementModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.GIVING_DAY_TEASER:
        return (
          <GivingDayTeaserModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SIGNUP_FORMS_REPLACED_BY_POPUPS:
        return (
          <SignupFormsReplacedByPopupsOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.CUSTOMS_INFORMATION_POPUP:
      case FeatureOnboardingType.INTERNATIONAL_RETURNS:
      case FeatureOnboardingType.CROSS_BORDER_ONBOARDING:
      case FeatureOnboardingType.OUTBOUND_VIEWS_MOVED:
      case FeatureOnboardingType.DISPUTES_WELCOME:
      case FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL:
      case FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE:
      case FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION:
      case FeatureOnboardingType.BLACK_FRIDAY_TIPS:
      case FeatureOnboardingType.PERMISSIONS_ROLES:
      case FeatureOnboardingType.OUTBOUND_LABELS:
      case FeatureOnboardingType.NAV_REORGANIZATION:
      case FeatureOnboardingType.SUPPORT_V2_NEW_LOOK:
        return null;
      // Cross-sell removed; kept for backward compatibility with existing DB records
      case FeatureOnboardingType.COMMENTSOLD_REDO:
        return null;
      default:
        assertNever(onboardingType);
    }
  }, [
    completeOnboardingType,
    dismissOnboardingType,
    handleComplete,
    handleDismiss,
    onboardingType,
    manualWithdrawalsOnboardingOpen,
    previewType,
    showOnboarding,
    screenType,
    withdrawalsOnboardingRequestId,
  ]);

  const routeSuppressed =
    isWmsRoute(location.pathname) || isWelcomeRoute(location.pathname);

  const popupVisible = isPopupVisibleToMerchant({
    hasCard: !!card,
    routeSuppressed,
    managedReturnsTrialPreview,
    renderedType: onboardingType,
  });

  /** Cleared when the popup hides so a later re-show is counted again. */
  const shownTrackedFor = useRef<FeatureOnboardingType | undefined>(undefined);
  useEffect(() => {
    if (!popupVisible || !onboardingType) {
      shownTrackedFor.current = undefined;
      return;
    }
    if (shownTrackedFor.current === onboardingType) {
      return;
    }
    shownTrackedFor.current = onboardingType;
    trackOnboardingEvent(PopupEvent.SHOWN, onboardingType);
  }, [popupVisible, onboardingType, trackOnboardingEvent]);

  // Suppress all feature-onboarding chrome inside the welcome flow — its own
  // elevated onboarding modal owns the screen there.
  if (routeSuppressed) {
    return null;
  }

  if (managedReturnsTrialPreview) {
    return (
      <ManagedReturnsTrialModal
        onClose={() => navigate(location.pathname, { replace: true })}
      />
    );
  }

  if (!card) {
    return null;
  }

  if (
    onboardingType === FeatureOnboardingType.WITHDRAWALS_ONBOARDING ||
    onboardingType === FeatureOnboardingType.MANAGED_RETURNS_TRIAL
  ) {
    return card;
  }

  const containerClass =
    onboardingType === FeatureOnboardingType.FEEDBACK_ANNOUNCEMENT
      ? styles.containerTopRight
      : styles.container;

  return <div className={containerClass}>{card}</div>;
});

function useManagedReturnsTrialPreview(): boolean {
  const { search } = useLocation();
  return useMemo(
    () => new URLSearchParams(search).has("managed-returns-trial-preview"),
    [search],
  );
}
